
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileDown, Plus, ArrowRight, RefreshCw, Play, Pause, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ETLPipelinesProps {
  editing: boolean;
}

const mockPipelines = [
  { 
    id: '1',
    name: 'Energy Consumption ETL',
    source: 'SQL Database',
    destination: 'Analytics Warehouse',
    schedule: 'Every 4 hours',
    status: 'active',
    lastRun: '2 hours ago',
  },
  { 
    id: '2',
    name: 'Sustainability Metrics',
    source: 'API Endpoint',
    destination: 'ESG Database',
    schedule: 'Daily at 00:00',
    status: 'active',
    lastRun: '23 hours ago',
  },
  { 
    id: '3',
    name: 'Carbon Footprint Import',
    source: 'CSV Upload',
    destination: 'Carbon Dashboard',
    schedule: 'Weekly on Sunday',
    status: 'paused',
    lastRun: '6 days ago',
  },
];

const ETLPipelines: React.FC<ETLPipelinesProps> = ({ editing }) => {
  const [pipelines, setPipelines] = useState(mockPipelines);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredPipelines = pipelines.filter(pipeline => 
    pipeline.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const togglePipelineStatus = (id: string) => {
    if (!editing) return;
    
    setPipelines(pipelines.map(pipeline => 
      pipeline.id === id ? {
        ...pipeline,
        status: pipeline.status === 'active' ? 'paused' : 'active'
      } : pipeline
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">ETL Pipelines</h3>
        
        {editing && (
          <Button size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Create New Pipeline
          </Button>
        )}
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Input
          placeholder="Search pipelines..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {filteredPipelines.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pipeline Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="hidden md:table-cell">Destination</TableHead>
                  <TableHead className="hidden md:table-cell">Schedule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPipelines.map(pipeline => (
                  <TableRow key={pipeline.id}>
                    <TableCell className="font-medium">{pipeline.name}</TableCell>
                    <TableCell>{pipeline.source}</TableCell>
                    <TableCell className="hidden md:table-cell">{pipeline.destination}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center">
                        <Clock className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                        {pipeline.schedule}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`${
                          pipeline.status === 'active' 
                            ? 'bg-green-50 text-green-700 hover:bg-green-50 border-green-200' 
                            : 'bg-amber-50 text-amber-700 hover:bg-amber-50 border-amber-200'
                        }`}
                      >
                        {pipeline.status === 'active' ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          disabled={!editing}
                          onClick={() => togglePipelineStatus(pipeline.id)}
                        >
                          {pipeline.status === 'active' ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button variant="outline" size="icon" disabled={!editing}>
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <FileDown className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No ETL Pipelines Found</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              {searchTerm ? 
                `No pipelines match "${searchTerm}". Try a different search term.` : 
                'Configure ETL pipelines to extract, transform, and load data from different sources.'}
            </p>
            {editing && (
              <Button>
                <Plus className="h-4 w-4 mr-1" />
                Create First Pipeline
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ETLPipelines;
